import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./StepForm.css"; // Ensure this points to the CORRECTED CSS below

// Define options for all yes/no dropdowns
const yesNoOptions = [
  { value: "YES", label: "YES" },
  { value: "NO", label: "NO" },
];
const parsingTypeOptions = [
  { value: "AGRICULTURE", label: "AGRICULTURE" },
  { value: "COMMERCIAL", label: "COMMERCIAL" },
];
const statusList = [
  { value: "Pending", label: "Pending" },
  { value: "Done", label: "Done" },
];

const NUMBER22_MIN = 6;
const NUMBER22_MAX = 8;

function StepForm({ stepIndex, stepTitle, initialData, onSave }) {
  const [formData, setFormData] = useState(initialData || {});
  const [number22Error, setNumber22Error] = useState("");

  useEffect(() => {
    // Reset form when initialData or stepIndex changes (modal reopens/changes step)
    setFormData(initialData || {});
    setNumber22Error("");
  }, [initialData, stepIndex]);

  // Auto-calculate total when insurance, tax, and penalty are all integer strings
  useEffect(() => {
    const { insurance = "", tax = "", penalty = "" } = formData;
    const isInteger = (str) => /^\d+$/.test(str);
    if (isInteger(insurance) && isInteger(tax) && isInteger(penalty)) {
      const sum =
        parseInt(insurance, 10) + parseInt(tax, 10) + parseInt(penalty, 10);
      const totalStr = sum.toString();
      if (formData.total !== totalStr) {
        setFormData((prev) => ({ ...prev, total: totalStr }));
      }
    }
  }, [formData.insurance, formData.tax, formData.penalty]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "number22") setNumber22Error("");
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (stepIndex === 6) {
      const val = (formData.number22 || "").trim();
      const len = val.length;
      if (len < NUMBER22_MIN || len > NUMBER22_MAX) {
        setNumber22Error(
          `22 Number must be between ${NUMBER22_MIN} and ${NUMBER22_MAX} characters.`
        );
        return;
      }
      setNumber22Error("");
    }
    console.log(`Saving data for step ${stepIndex} (${stepTitle}):`, formData);
    onSave(stepIndex, formData);
  };

  // --- Render different form fields based on stepIndex ---
  const renderFormFields = () => {
    switch (stepIndex) {
      case 0: // CHASSIS PRINT
        return (
          <>
            <div className="form-group">
              <label
                htmlFor={`chassis_print_${stepIndex}`}
                className="form-label"
              >
                Chassis Print <span className="text-danger">*</span>
              </label>
              <select
                id={`chassis_print_${stepIndex}`}
                name="chassis_print"
                value={formData.chassis_print || ""}
                onChange={handleChange}
                className="form-input"
              >
                <option value="" label="Select option" />
                {yesNoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor={`notes_${stepIndex}`}>Notes / Comments:</label>
              <textarea
                id={`notes_${stepIndex}`}
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows="4"
                placeholder="Add any relevant notes, observations, or issues..."
              />
            </div>
          </>
        );

      case 1: // DOCUMENTS
        return (
          <>
            <div className="form-group">
              <label htmlFor={`bank_passbook_${stepIndex}`} className="form-label">
                Bank Passbook <span className="text-danger">*</span>
              </label>
              <select
                id={`bank_passbook_${stepIndex}`}
                name="bank_passbook"
                value={formData.bank_passbook || ""}
                onChange={handleChange}
                className="form-input"
              >
                <option value="" label="Select option" />
                {yesNoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor={`aadhar_card_${stepIndex}`} className="form-label">
                Aadhar Card <span className="text-danger">*</span>
              </label>
              <select
                id={`aadhar_card_${stepIndex}`}
                name="aadhar_card"
                value={formData.aadhar_card || ""}
                onChange={handleChange}
                className="form-input"
              >
                <option value="" label="Select option" />
                {yesNoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor={`parsing_type_${stepIndex}`} className="form-label">
                Vehicle Type <span className="text-danger">*</span>
              </label>
              <select
                id={`parsing_type_${stepIndex}`}
                name="parsing_type"
                value={formData.parsing_type || ""}
                onChange={handleChange}
                className="form-input"
              >
                <option value="" label="Select option" />
                {parsingTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {formData.parsing_type === "AGRICULTURE" && (
              <div className="form-group">
                <label htmlFor={`jamin_no_${stepIndex}`} className="form-label">
                  Jamin Khata No <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id={`jamin_no_${stepIndex}`}
                  name="jamin_no"
                  value={formData.jamin_no || ""}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter Jamin Khata No"
                />
              </div>
            )}
            {formData.jamin_no && (
              <div className="form-group">
                <label htmlFor={`subsidy_${stepIndex}`} className="form-label">
                  Subsidy Process <span className="text-danger">*</span>
                </label>
                <select
                  id={`subsidy_${stepIndex}`}
                  name="subsidy"
                  value={formData.subsidy || ""}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="" label="Select option" />
                  {yesNoOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label htmlFor={`notes_${stepIndex}`}>Notes / Comments:</label>
              <textarea
                id={`notes_${stepIndex}`}
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows="4"
                placeholder="Add any relevant notes, observations, or issues..."
              />
            </div>
          </>
        );

      case 2: // PARSING COMPLETE
        return (
          <>
            <div className="form-group">
              <label htmlFor={`retail_bill_no_${stepIndex}`}>
                RETAIL BILL NO:
              </label>
              <input
                type="text"
                id={`retail_bill_no_${stepIndex}`}
                name="retail_bill_no"
                value={formData.retail_bill_no || ""}
                onChange={handleChange}
                placeholder="RETAIL BILL NO"
              />
            </div>
            <div className="form-group">
              <label htmlFor={`insurance_${stepIndex}`}>
                INSURANCE AMOUNT:
              </label>
              <input
                type="text"
                id={`insurance_${stepIndex}`}
                name="insurance"
                value={formData.insurance || ""}
                onChange={handleChange}
                placeholder="VIMO / INSURANCE AMOUNT"
              />
            </div>
            <div className="form-group">
              <label htmlFor={`tax_${stepIndex}`}>TAX AMOUNT:</label>
              <input
                type="text"
                id={`tax_${stepIndex}`}
                name="tax"
                value={formData.tax || ""}
                onChange={handleChange}
                placeholder="TAX AMOUNT"
              />
            </div>
            <div className="form-group">
              <label htmlFor={`penalty_${stepIndex}`}>PENALTY AMOUNT:</label>
              <input
                type="text"
                id={`penalty_${stepIndex}`}
                name="penalty"
                value={formData.penalty || ""}
                onChange={handleChange}
                placeholder="PENALTY AMOUNT"
              />
            </div>
            <div className="form-group">
              <label htmlFor={`total_${stepIndex}`}>TOTAL:</label>
              <input
                type="text"
                id={`total_${stepIndex}`}
                name="total"
                value={formData.total || ""}
                onChange={handleChange}
                placeholder="TOTAL"
              />
            </div>
            <div className="form-group">
              <label htmlFor={`registration_no_${stepIndex}`}>
                TRACTOR NO/REGISTRATION NO:
              </label>
              <input
                type="text"
                id={`registration_no_${stepIndex}`}
                name="registration_no"
                value={formData.registration_no || ""}
                onChange={handleChange}
                placeholder="TRACTOR NO"
              />
            </div>
            <div className="form-group">
              <label htmlFor={`notes_${stepIndex}`}>Notes / Comments:</label>
              <textarea
                id={`notes_${stepIndex}`}
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows="4"
                placeholder="Add any relevant notes, observations, or issues..."
              />
            </div>
          </>
        );
      case 3: // PLATE ORDER
        return (
          <>
            <div className="form-group">
              <label
                htmlFor={`plate_order_${stepIndex}`}
                className="form-label"
              >
                PLATE ORDER <span className="text-danger">*</span>
              </label>
              <select
                id={`plate_order_${stepIndex}`}
                name="plate_order"
                value={formData.plate_order || ""}
                onChange={handleChange}
                className="form-input"
              >
                <option value="" label="Select option" />
                {yesNoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor={`notes_${stepIndex}`}>Notes / Comments:</label>
              <textarea
                id={`notes_${stepIndex}`}
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows="4"
                placeholder="Add any relevant notes, observations, or issues..."
              />
            </div>
          </>
        );

      case 4: // FITMATE SETUP
        return (
          <>
            <div className="form-group">
              <label htmlFor={`rc_fitment_${stepIndex}`} className="form-label">
                RC FITMATE <span className="text-danger">*</span>
              </label>
              <select
                id={`rc_fitment_${stepIndex}`}
                name="rc_fitment"
                value={formData.rc_fitment || ""}
                onChange={handleChange}
                className="form-input"
              >
                <option value="" label="Select option" />
                {yesNoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor={`notes_${stepIndex}`}>Notes / Comments:</label>
              <textarea
                id={`notes_${stepIndex}`}
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows="4"
                placeholder="Add any relevant notes, observations, or issues..."
              />
            </div>
          </>
        );
      
      // PLAT FITTED DISPATCH (RC DISPATCH)
      case 5:
        return (
          <>
            <div className="form-group">
              <label htmlFor={`person_name_${stepIndex}`} className="form-label">
                Person Name
              </label>
              <input
                type="text"
                id={`person_name_${stepIndex}`}
                name="person_name"
                value={formData.person_name || ""}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter the person's name"
              />
            </div>
            <div className="form-group">
              <label htmlFor={`date_of_fitting_${stepIndex}`} className="form-label">
                Date of Fitting
              </label>
              <input
                type="date"
                id={`date_of_fitting_${stepIndex}`}
                name="date_of_fitting"
                value={formData.date_of_fitting || ""}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor={`notes_${stepIndex}`}>Remarks/notes:</label>
              <textarea
                id={`notes_${stepIndex}`}
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows="4"
                placeholder="Add any relevant remarks or notes..."
              />
            </div>
          </>
        );

      // 22 Number step: only 22 Number field (min 6, max 8 chars)
      case 6:
        return (
          <>
            <div className="form-group">
              <label htmlFor={`number22_${stepIndex}`} className="form-label">
                22 Number <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id={`number22_${stepIndex}`}
                name="number22"
                value={formData.number22 || ""}
                onChange={handleChange}
                className={`form-input${number22Error ? " is-invalid" : ""}`}
                placeholder={`Enter 22 Number (${NUMBER22_MIN}-${NUMBER22_MAX} characters)`}
                minLength={NUMBER22_MIN}
                maxLength={NUMBER22_MAX}
              />
              {number22Error && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {number22Error}
                </div>
              )}
            </div>
          </>
        );

      default:
        return (
          <>
            <div className="form-group form-group-checkbox">
              <label htmlFor={`isComplete_${stepIndex}`}>
                <input
                  type="checkbox"
                  id={`isComplete_${stepIndex}`}
                  name="isComplete"
                  checked={formData.isComplete || false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isComplete: e.target.checked,
                    }))
                  }
                />
                <span className="checkbox-label-text">
                  Mark this step as complete?
                </span>
              </label>
            </div>
            <div className="form-group">
              <label htmlFor={`notes_${stepIndex}`}>Notes:</label>
              <textarea
                id={`notes_${stepIndex}`}
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows="4"
                placeholder="Add any final notes for this step..."
              />
            </div>
          </>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="step-form professional">
      {renderFormFields()}
      <div className="form-actions">
        <button type="submit" className="form-submit-button">
          Save Data
        </button>
      </div>
    </form>
  );
}

StepForm.propTypes = {
  stepIndex: PropTypes.number.isRequired,
  stepTitle: PropTypes.string.isRequired,
  initialData: PropTypes.object,
  onSave: PropTypes.func.isRequired,
};

export default StepForm;